/**
 * The DKIM selector probe list, grouped by the provider that publishes each selector.
 *
 * This is the published, human-readable face of `apps/api/src/domain-health/dkim-selectors.ts` —
 * the exact list `/tools/dkim-checker/` probes. Two surfaces consume it:
 *
 *   - `/learn/dkim-selector/` renders it as the provider table. That table is the reason the page
 *     is worth writing: it is code-true rather than copied from a competitor, and nobody else
 *     publishes the mapping as one table.
 *   - `/tools/dkim-checker/` takes the COUNT from it, so the tool copy can never again claim a
 *     number the code disagrees with. It used to say "the 14 most common DKIM selectors" and name
 *     six (mailgun, k2, k3, m1, m2, m3, mx) the prober has never contained.
 *
 * The grouping is a strict PARTITION of the probe list: every selector appears in exactly one
 * group, and every probed selector appears somewhere. `apps/web/test/dkim-selectors.spec.ts`
 * pins both directions against the API source, so adding a selector to the prober without
 * placing it in the table (or vice versa) fails CI rather than silently publishing a stale table.
 *
 * `note` is for the caveat that cannot be expressed as a selector: providers that mint a
 * per-account token instead of a fixed word are undiscoverable by ANY probe list, and the page
 * says so rather than implying the table is exhaustive.
 */
export interface DkimProviderGroup {
  /** Provider as a sender would recognise it — the string people search for. */
  provider: string;
  /** Selectors this provider publishes. Order is presentational only. */
  selectors: readonly string[];
  /** Optional caveat rendered in the table row. */
  note?: string;
}

export const DKIM_PROVIDER_GROUPS: readonly DkimProviderGroup[] = [
  { provider: 'Google Workspace', selectors: ['google'] },
  { provider: 'Microsoft 365 / Outlook', selectors: ['selector1', 'selector2'] },
  { provider: 'Mailchimp / Mandrill', selectors: ['k1', 'mandrill'] },
  { provider: 'SendGrid', selectors: ['s1', 's2'] },
  {
    provider: 'Amazon SES',
    selectors: ['amazonses'],
    note: 'Easy DKIM only — BYODKIM mints a per-account token no probe can find.',
  },
  { provider: 'Brevo (formerly Sendinblue)', selectors: ['brevo1', 'brevo2', 'sib1', 'sib2'] },
  { provider: 'Zoho Mail', selectors: ['key1', 'key2', 'zmail'] },
  { provider: 'Proton Mail', selectors: ['protonmail', 'protonmail2', 'protonmail3'] },
  { provider: 'MailerLite', selectors: ['mlsend1', 'mlsend2'] },
  { provider: 'Constant Contact', selectors: ['ctct1', 'ctct2'] },
  { provider: 'Mailjet', selectors: ['mailjet'] },
  { provider: 'Namecheap Private Email', selectors: ['privateemail'] },
  { provider: 'Mailo', selectors: ['mailo'] },
  { provider: 'MXVault and similar filtering gateways', selectors: ['mxvault'] },
  {
    provider: 'Generic / self-hosted (Postfix + OpenDKIM, cPanel, Plesk)',
    selectors: ['default', 'mail', 'dkim', 'dkim1', 'dkim2', 'smtp', 'mta'],
  },
  {
    provider: 'Other selectors seen in the wild',
    selectors: ['pf2014', 'fd', 'fd2'],
    note: 'Carried over from older hosting stacks; kept because removing one produces false negatives.',
  },
];

/** How many selectors the checker probes. Never hardcode this number in copy — read it here. */
export const PROBED_SELECTOR_COUNT = DKIM_PROVIDER_GROUPS.reduce(
  (n, g) => n + g.selectors.length,
  0,
);
